'use client';

import { Button } from '@curology/ui-components-web/react/Button';
import { Typography } from '@curology/ui-components-web/react/Typography';
import Image from 'next/image';
import React, { useState, useEffect } from 'react';

import { useNavigation } from '@/components/flow/hooks/useNavigation';
import { useRouter } from '@/hooks/useRouter';
import { type ResubscriptionOutcomesStep } from '@/services/funnelService/models/resubscriptionOutcomesStep';
import { ResubscriptionService } from '@/services/resubscriptionService/client';
import { urls } from '@/utils/urls';

import { useFlowContext } from '../../hooks/useFlowContext';

export const ResubscriptionOutcomes = ({
  step,
}: {
  step: ResubscriptionOutcomesStep;
}) => {
  const { brand } = useFlowContext();
  const router = useRouter();
  const { onNext } = useNavigation();
  const {
    contentfulImageUrl,
    showImageFirst = false,
    title,
    description,
    nextBtnText = 'Next',
    ctaBtn = false,
    ctaBtnText,
    ctaDescription,
    ctaPath,
  } = step;

  const [isNavigating, setIsNavigating] = useState(false);
  const [previousResponses, setPreviousResponses] = useState<{
    [key: string]: string;
  }>({});
  const [patientWantsPreviousFormula, setPatientWantsPreviousFormula] =
    useState('no');

  useEffect(() => {
    const getPreviousSurveyResponses = async () => {
      const previousSurveyResponses =
        await ResubscriptionService.getPreviousResponses(brand);
      setPreviousResponses(previousSurveyResponses.data.data.previousResponses);
    };
    getPreviousSurveyResponses().catch(console.error);
  }, [brand]);

  useEffect(() => {
    setPatientWantsPreviousFormula(
      previousResponses['continuePreviousFormula'],
    );
  }, [previousResponses]);

  const onCtaClick = () => {
    if (isNavigating) {
      return;
    }

    setIsNavigating(true);
    router.push(`${urls[brand].legacy.url}/${ctaPath}`);
  };

  const titleAndDescription = (
    <>
      <Typography className="cur-leading-9" size="h2">
        {title}
      </Typography>
      {description && (
        <Typography size="body" className="cur-leading-relaxed">
          <div dangerouslySetInnerHTML={{ __html: description }}></div>
        </Typography>
      )}
    </>
  );

  const showCta = ctaBtn && patientWantsPreviousFormula === 'yes';

  return (
    <section className="cur-text-primary">
      <div className="cur-px-4 cur-py-8 cur-mx-auto">
        <div className="cur-max-w-sm cur-my-0 cur-mx-auto cur-relative cur-flex cur-flex-col cur-gap-6">
          {!showImageFirst && titleAndDescription}
          <div className="cur-mx-auto">
            {contentfulImageUrl && (
              <Image
                priority
                src={contentfulImageUrl}
                alt={'Hero image'}
                quality={100}
                width={375}
                height={275}
                className="cur-mx-auto"
              />
            )}
          </div>
          {showImageFirst && titleAndDescription}
          {showCta && ctaDescription}
          <div className="cur-flex cur-flex-col cur-gap-y-2">
            {showCta && (
              <Button
                className="cur-w-full"
                onClick={onCtaClick}
                loading={isNavigating}
              >
                {ctaBtnText}
              </Button>
            )}
            <Button
              buttonType={showCta ? 'secondary' : 'primary'}
              onClick={onNext}
            >
              {nextBtnText}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
